import React, { useState } from 'react';
import Modal from 'react-modal';
import LoadingOverlay from 'components/LoadingOverlay';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import './CopyBundle.scss';

function CopyBundleModal({
  onClose,
  onCloseAll,
  copyToPurchaseTypes,
  sourceBundle,
  dealer,
  updateDealer,
}) {
  const [isLoading, setLoading] = useState(false);
  const [isCompleted, setCompleted] = useState(false);
  const icons = {
    alert: <span className="glyphicon glyphicon-alert" />,
    ok: <span className="glyphicon glyphicon-ok" />,
    exclamation: <span className="glyphicon glyphicon-exclamation-sign" />,
    ban: <span className="glyphicon glyphicon-ban-circle" />,
  };
  const copyProducts = sourceBundle.productIds
    ? dealer.penProducts.filter((product) =>
        sourceBundle.productIds.includes(product.id),
      )
    : [];

  const isAvailableProduct = (product, purchaseType) => {
    const productCondition = product.availability[purchaseType.condition];
    return productCondition && productCondition[purchaseType.financeType];
  };

  const bundleExists = (purchaseType) => {
    const condition = dealer.penConfig.bundles[purchaseType.condition];
    const destinationBundles = condition && condition[purchaseType.financeType];
    if (destinationBundles && destinationBundles.length) {
      return destinationBundles.some(
        (bundle) => bundle.name === sourceBundle.name,
      );
    }
    return false;
  };

  const changes = copyToPurchaseTypes.map((purchaseType) => {
    const productChanges = copyProducts.map((product) => ({
      hasConflict: !isAvailableProduct(product, purchaseType),
      product,
    }));

    return {
      purchaseType,
      productChanges,
      willCopy: productChanges.some((product) => !product.hasConflict),
      nameExists: bundleExists(purchaseType),
    };
  });

  const onSave = async () => {
    setLoading(true);
    const newDealer = { id: dealer.id, penConfig: cloneDeep(dealer.penConfig) };
    for (const change of changes) {
      const { id, ...newBundle } = sourceBundle;
      if (change.willCopy) {
        newBundle.id = uuidv4();
        newBundle.productIds = [];
        for (const productChange of change.productChanges) {
          if (!productChange.hasConflict) {
            newBundle.productIds.push(productChange.product.id);
          }
        }

        if (change.nameExists) {
          const currentBundles =
            newDealer.penConfig.bundles[change.purchaseType.condition][
              change.purchaseType.financeType
            ];
          const idx = currentBundles.findIndex(
            (bundle) => bundle.name === sourceBundle.name,
          );
          currentBundles[idx] = newBundle;
          newDealer.penConfig.bundles[change.purchaseType.condition][
            change.purchaseType.financeType
          ] = currentBundles;
        } else {
          newDealer.penConfig.bundles[change.purchaseType.condition][
            change.purchaseType.financeType
          ].push(newBundle);
        }
      }
    }

    await updateDealer({ dealer: newDealer });
    setCompleted(true);
    setLoading(false);
  };

  const displayProductChanges = (change) =>
    change.productChanges.map((productChange) =>
      productChange.hasConflict ? (
        <div className="item desc" key={productChange.product.id}>
          {icons.ban}
          <span className="strike">{productChange.product.displayName}</span>
        </div>
      ) : (
        <div className="item desc" key={productChange.product.id}>
          {icons.ok}
          <span>{productChange.product.displayName}</span>
        </div>
      ),
    );

  const displayBundleChange = (change) => {
    if (!change.willCopy) {
      return (
        <div className="item desc">
          {icons.alert}
          <span>Bundle will not be copied; products ineligible</span>
        </div>
      );
    }
    if (change.nameExists) {
      return (
        <div className="item desc">
          {icons.exclamation}
          <span>
            Copying this bundle will override a bundle with the same name
          </span>
        </div>
      );
    }
    return null;
  };

  const displaysChanges = (change) => (
    <div>
      {displayBundleChange(change)}
      {change.willCopy && displayProductChanges(change)}
    </div>
  );

  const renderConfirmation = () => (
    <>
      <div className="confirmation">
        <div className="source">
          <div className="item">
            <strong>Bundle copied</strong>
          </div>
          {
            <div className="item purchaseType">
              <strong className="capitalize">{`${sourceBundle.purchaseType.condition} - ${sourceBundle.purchaseType.financeType} | ${sourceBundle.name}`}</strong>
              {copyProducts.map((product) => (
                <div key={product.id} className="item">
                  {product.displayName}
                </div>
              ))}
            </div>
          }
        </div>
        <div className="copyTo">
          <strong>Bundle changes ({changes.length})</strong>
          {changes.map((change) => (
            <div
              className="item purchaseType"
              key={change.purchaseType.id}
              label={change.purchaseType.id}
            >
              <strong className="capitalize">{`${change.purchaseType.condition} - ${change.purchaseType.financeType} | ${sourceBundle.name}`}</strong>
              {displaysChanges(change)}
              <hr />
            </div>
          ))}
        </div>
      </div>
      <div className="actions">
        <div className="actionDesc">
          Continuing will <strong>override</strong> existing bundles with the
          same name.
          <br />
          This cannot be undone.
        </div>
        <button className="btn btn-default cancelBtn" onClick={onClose}>
          Cancel
        </button>
        <button
          disabled={!changes.some((change) => change.willCopy)}
          className="btn btn-primary copySaveBtn"
          onClick={onSave}
        >
          Confirm Changes
        </button>
      </div>
    </>
  );

  const renderCompleted = () => {
    const results = [];
    for (const change of changes) {
      if (change.willCopy) {
        results.push(
          <div key={change.purchaseType.id} className="item">
            {icons.ok}
            <span className="capitalize">{`${change.purchaseType.condition} - ${change.purchaseType.financeType}`}</span>
          </div>,
        );
      }
    }
    return (
      <>
        <div className="completed">{results}</div>
        <button className="btn btn-default closeBtn" onClick={onCloseAll}>
          Close
        </button>
      </>
    );
  };

  return (
    <Modal isOpen contentLabel="copyModalContent" ariaHideApp={false}>
      <LoadingOverlay
        active={isLoading}
        className={`copyBundleContainer ${
          isCompleted ? 'completedContainer' : ''
        }`}
      >
        <div className="modalHeader">
          <strong className="capitalize">
            {isCompleted
              ? 'Bundle has been applied to the following purchase types'
              : 'Please confirm your changes'}
          </strong>
          <i
            onClick={isCompleted ? onCloseAll : onClose}
            className="close icon"
            alt="close"
          />
        </div>
        {isCompleted ? renderCompleted() : renderConfirmation()}
      </LoadingOverlay>
    </Modal>
  );
}

CopyBundleModal.propTypes = {
  onClose: PropTypes.func,
  onCloseAll: PropTypes.func,
  copyToPurchaseTypes: PropTypes.array,
  sourceBundle: PropTypes.object,
  dealer: PropTypes.object,
  updateDealer: PropTypes.func,
};

export default CopyBundleModal;
